import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Area, AreaChart, ResponsiveContainer, ReferenceLine } from "recharts";
import { api } from "../lib/api";

// Cache quotes for a few seconds to avoid hammering the API on hover.
const cache = new Map();
const TTL = 30_000;

async function getQuote(ticker, tf) {
  const key = `${ticker}:${tf}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.t < TTL) return hit.data;
  const { data } = await api.get(`/api/scanner/quote/${ticker}?timeframe=${tf}`);
  cache.set(key, { t: Date.now(), data });
  return data;
}

/**
 * Floating mini chart that follows the cursor.
 * Mount once at the page root. The `target` prop is the element whose hover triggers it.
 */
export default function MiniChartPopover({ ticker, timeframe = "15m", anchorRect, onClose }) {
  const [data, setData] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const ref = useRef(null);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    getQuote(ticker, timeframe).then((d) => { if (!cancelled) setData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [ticker, timeframe]);

  useEffect(() => {
    if (!anchorRect) return;
    const W = 340, H = 220, M = 12;
    let x = anchorRect.right + M;
    let y = anchorRect.top - 20;
    if (x + W > window.innerWidth - 12) x = anchorRect.left - W - M;
    if (x < 12) x = 12;
    if (y + H > window.innerHeight - 12) y = window.innerHeight - H - 12;
    if (y < 12) y = 12;
    setPos({ x, y });
  }, [anchorRect]);

  if (!ticker || !anchorRect) return null;

  const candles = data?.candles || [];
  const ind = data?.indicators;
  const chartData = candles.map((c, i) => ({ i, c: c.c }));
  const change = candles.length >= 2 ? ((candles[candles.length - 1].c - candles[0].c) / candles[0].c) * 100 : 0;
  const isUp = change >= 0;
  const color = isUp ? "#00ff9c" : "#ff4d6d";

  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: 340,
        zIndex: 100,
        pointerEvents: "none",
      }}
      className="fade-in"
      data-testid={`mini-chart-${ticker}`}
    >
      <div
        className="glass-strong rounded-2xl p-4"
        style={{
          background: "rgba(14,14,18,0.92)",
          boxShadow: "0 32px 80px -20px rgba(0,0,0,0.7), 0 0 60px -10px rgba(108,92,231,0.3)",
        }}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="text-white text-[15px] font-semibold tracking-tight">{ticker}</div>
            <div className="label-mono mt-0.5">{timeframe} · live preview</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-white text-base tabular-nums">
              {data?.price ? data.price.toFixed(2) : "…"}
            </div>
            <div className={`font-mono text-[11px] ${isUp ? "text-bull" : "text-bear"}`}>
              {data ? `${isUp ? "+" : ""}${change.toFixed(2)}%` : "—"}
            </div>
          </div>
        </div>
        <div style={{ height: 96 }}>
          {chartData.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 6, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={`mini-${ticker}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.55} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                {ind?.vwap && <ReferenceLine y={ind.vwap} stroke="rgba(255,255,255,0.4)" strokeDasharray="3 3" />}
                <Area type="monotone" dataKey="c" stroke={color} strokeWidth={2} fill={`url(#mini-${ticker})`} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        {ind && (
          <div className="grid grid-cols-4 gap-2 mt-2.5 pt-2.5 border-t border-white/5 text-center">
            <Stat label="RSI" value={ind.rsi.toFixed(1)} />
            <Stat label="MACD" value={ind.macd.toFixed(3)} cls={ind.macd_hist >= 0 ? "text-[var(--bull)]" : "text-[var(--bear)]"} />
            <Stat label="VWAP" value={ind.vwap.toFixed(2)} />
            <Stat label="RVOL" value={ind.rvol.toFixed(2)} />
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function Stat({ label, value, cls }) {
  return (
    <div>
      <div className="label-mono">{label}</div>
      <div className={`font-mono text-[12px] mt-0.5 tabular-nums ${cls || "text-white"}`}>{value}</div>
    </div>
  );
}
